import { useEffect, useId, useRef } from 'react';
import { useGlobalStore } from '@/stores/globalStore';

/**
 * Custom hook to register a loading state for a component.
 * The page loader will show until all registered components have finished loading.
 *
 * @param isLoading - Whether this component is currently loading
 * @param key - Optional unique identifier. If not provided, auto-generates one.
 *
 * @example
 * ```tsx
 * const [isLoading, setIsLoading] = useState(true);
 * usePageLoading(isLoading);
 *
 * // When your data loads:
 * setIsLoading(false);
 * ```
 *
 * @example With custom key (useful for debugging)
 * ```tsx
 * usePageLoading(isLoading, 'income-chart');
 * ```
 */
export function usePageLoading(isLoading: boolean, key?: string) {
    const autoGeneratedId = useId();
    const loadingKey = key ?? autoGeneratedId;
    const registerLoading = useGlobalStore(state => state.registerLoading);
    const unregisterLoading = useGlobalStore(state => state.unregisterLoading);
    const hasRegistered = useRef(false);

    useEffect(() => {
        if (isLoading && !hasRegistered.current) {
            registerLoading(loadingKey);
            hasRegistered.current = true;
        } else if (!isLoading && hasRegistered.current) {
            unregisterLoading(loadingKey);
            hasRegistered.current = false;
        }

        // Cleanup: unregister on unmount
        return () => {
            if (hasRegistered.current) {
                unregisterLoading(loadingKey);
                hasRegistered.current = false;
            }
        };
    }, [isLoading, loadingKey, registerLoading, unregisterLoading]);
}
